"use client";

import AutocompleteSingleSelect from "@/components/Autocomplete/Autocomplete";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import toId from "@/utils/toId.utils";
import { FieldValues, useFormContext } from "react-hook-form";
import { AutocompleteFieldProps } from "./autocomplet.types";

/**
 * RHF wrapper around `AutocompleteSingleSelect`.
 *
 * Renders a labeled, accessible form field with description and error message.
 * Must be rendered inside a shadcn `<Form>` provider.
 *
 * @example
 * <AutocompleteSingleSelectField
 *   name="department"
 *   label="Department"
 *   options={departmentOptions}
 *   required
 * />
 */
function AutocompleteSingleSelectField<T extends FieldValues = FieldValues>({
  // RHF
  name,
  // Options
  options,
  // Form layout
  label,
  description,
  required = false,
  // Autocomplete behaviour
  placeholder,
  emptyMessage,
  disabled = false,
  showClear = true,
  autoHighlight = false,
  // Override hooks
  value: externalValue,
  onValueChange,
  // Styling
  className,
  // Accessibility
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: AutocompleteFieldProps<T>) {
  const { control } = useFormContext<T>();

  // CSS-safe ID derived from field name (e.g. "address.city" → "address-city")
  const safeId = toId(name);

  // Only create descriptionId when a description will actually render,
  // so aria-describedby doesn't point at a non-existent element.
  const descriptionId = description ? `${safeId}-description` : undefined;
  const errorId = `${safeId}-error`;

  const {
    itemClass,
    labelClass,
    descriptionClass,
    messageClass,
    autocompleteClass,
  } = className ?? {};

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const ariaDescribedBy =
          [descriptionId, fieldState.error ? errorId : undefined]
            .filter(Boolean)
            .join(" ") || undefined;

        // externalValue overrides RHF (e.g. URL-synced forms).
        // RHF gives undefined for unset fields — normalize to null.
        const resolvedValue: string | null =
          externalValue !== undefined ? externalValue : (field.value ?? null);

        // Always call field.onChange to keep RHF in sync.
        // onValueChange is an optional side-effect (URL push, analytics, etc.)
        const handleValueChange = (selected: string | null) => {
          field.onChange(selected);
          onValueChange?.(selected);
        };

        return (
          <FormItem className={cn("space-y-0", itemClass)}>
            <FormLabel
              htmlFor={safeId}
              className={cn(
                "flex items-center gap-1 text-sm font-medium",
                !label && "sr-only", // hide visually but keep for screen readers
                labelClass
              )}
            >
              <span>{label ?? name}</span>
              {required && (
                // Decorative — aria-required on the input handles a11y
                <span className="text-destructive" aria-hidden="true">
                  *
                </span>
              )}
            </FormLabel>

            {/*
             * Props are passed explicitly because AutocompleteSingleSelect
             * renders a custom Base UI input that FormControl cannot reach directly.
             */}
            <FormControl>
              <AutocompleteSingleSelect
                options={options}
                value={resolvedValue}
                onValueChange={handleValueChange}
                placeholder={placeholder}
                emptyMessage={emptyMessage}
                disabled={disabled}
                showClear={showClear}
                autoHighlight={autoHighlight}
                id={safeId}
                aria-label={ariaLabel ?? label}
                aria-labelledby={ariaLabelledBy}
                aria-describedby={ariaDescribedBy}
                aria-invalid={fieldState.error ? "true" : "false"}
                aria-required={required}
                onBlur={field.onBlur}
                className={autocompleteClass}
              />
            </FormControl>

            {description && (
              <FormDescription
                id={descriptionId}
                className={cn("text-muted-foreground text-sm", descriptionClass)}
              >
                {description}
              </FormDescription>
            )}

            {/* id is always present so aria-describedby can safely reference it
                before an error appears — screen readers ignore empty elements. */}
            <FormMessage id={errorId} className={cn("text-sm", messageClass)} />
          </FormItem>
        );
      }}
    />
  );
}

export default AutocompleteSingleSelectField;
